import {
  Box,
  Button,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/PersonOutline';
import ShieldIcon from '@mui/icons-material/ShieldOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ROUTES } from '@/shared/config/routes';
import { APP_NAME } from '@/shared/config/env';
import { Logo } from '@/shared/ui';
import { UserAvatar, useSession } from '@/entities/user';
import { ThemeSwitcher } from '@/features/theme-switcher';
import { LanguageSwitcher } from '@/features/language-switcher';
import { NAV_ITEMS, isActiveRoute } from '../model/nav-items';

interface NavDrawerProps {
  open: boolean;
  onClose: () => void;
  onSignOut: () => void;
}

/**
 * Everything the top bar cannot show on a phone, in one panel.
 *
 * On a narrow screen the bar had three section buttons, two icon menus and an avatar in a
 * single row, and they simply ran off the edge — the sections were the first to go, which
 * left the app with no way to navigate at all. One panel holds the lot, so a new entry
 * costs nothing and nothing has to be dropped to make room.
 *
 * It opens from the right because that is the side the button is on: a panel that appears
 * away from the thumb that summoned it reads as a different control.
 */
export function NavDrawer({ open, onClose, onSignOut }: NavDrawerProps) {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { user, hasPermission } = useSession();

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      slotProps={{ paper: { sx: { width: 300, maxWidth: '85vw' } } }}
    >
      <Stack sx={{ height: '100%' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ px: 2, py: 1.5 }}>
          <Logo color="primary" sx={{ fontSize: 24 }} />
          <Typography variant="subtitle1" fontWeight={700} sx={{ flexGrow: 1 }}>
            {APP_NAME}
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label={t('nav.closeMenu')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>

        <Divider />

        {/* Who you are comes first: on a phone the avatar is no longer in the bar, and
            "am I signed in?" is the question the panel is opened with most often. */}
        <Box sx={{ px: 2, py: 2 }}>
          {user ? (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <UserAvatar user={user} size={40} />
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {user.displayName ?? user.email}
                </Typography>
                {user.displayName && (
                  <Typography variant="caption" color="text.secondary" noWrap display="block">
                    {user.email}
                  </Typography>
                )}
              </Box>
            </Stack>
          ) : (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                {t('nav.signedOut')}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  fullWidth
                  variant="contained"
                  component={RouterLink}
                  to="/login"
                  onClick={onClose}
                >
                  {t('nav.signIn')}
                </Button>
                <Button
                  fullWidth
                  variant="outlined"
                  component={RouterLink}
                  to="/register"
                  onClick={onClose}
                >
                  {t('nav.signUp')}
                </Button>
              </Stack>
            </Stack>
          )}
        </Box>

        <Divider />

        <List sx={{ px: 1, py: 1, flexGrow: 1 }}>
          {NAV_ITEMS.filter((item) => !item.authenticatedOnly || Boolean(user)).map((item) => {
            const active = isActiveRoute(pathname, item.to);
            const Icon = item.icon;

            return (
              <ListItemButton
                key={item.to}
                component={RouterLink}
                to={item.to}
                onClick={onClose}
                selected={active}
                sx={{ borderRadius: 2, mb: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 40, color: active ? 'primary.main' : undefined }}>
                  <Icon />
                </ListItemIcon>
                <ListItemText
                  primary={t(item.key)}
                  slotProps={{
                    primary: { fontWeight: active ? 600 : 400 },
                  }}
                />
              </ListItemButton>
            );
          })}

          {user && (
            <>
              <Divider sx={{ my: 1 }} />

              <ListItemButton
                component={RouterLink}
                to="/profile"
                onClick={onClose}
                selected={isActiveRoute(pathname, '/profile')}
                sx={{ borderRadius: 2, mb: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <PersonIcon />
                </ListItemIcon>
                <ListItemText primary={t('nav.profile')} />
              </ListItemButton>

              {hasPermission('users:read') && (
                <ListItemButton
                  component={RouterLink}
                  to={ROUTES.admin}
                  onClick={onClose}
                  selected={isActiveRoute(pathname, ROUTES.admin)}
                  sx={{ borderRadius: 2 }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <ShieldIcon />
                  </ListItemIcon>
                  <ListItemText primary={t('nav.administration')} />
                </ListItemButton>
              )}
            </>
          )}
        </List>

        <Divider />

        {/* Language and theme are choices about this device, so they sit apart from the
            account block rather than inside it. */}
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: (theme) => alpha(theme.palette.text.primary, 0.03),
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
            {t('nav.settings')}
          </Typography>
          <LanguageSwitcher />
          <ThemeSwitcher />
        </Stack>

        {user && (
          <>
            <Divider />
            <ListItemButton
              onClick={() => {
                onClose();
                onSignOut();
              }}
              sx={{ py: 1.5 }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText primary={t('nav.signOut')} />
            </ListItemButton>
          </>
        )}
      </Stack>
    </Drawer>
  );
}
